import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import MoneyInput from "../../../../Components/Common/MoneyInput";
import {
  formatCentsToMoneyInput,
  formatMoneyFromCents,
  parseMoneyInputToCents,
} from "../../../../helpers/money";
import {
  ClientSubscriptionPlan,
  ClubMetrics,
  createClientSubscriptionPlan,
  deleteClientSubscriptionPlan,
  getClientSubscriptionMetrics,
  getClientSubscriptionPlans,
  updateClientSubscriptionPlan,
} from "../../../../services/clientSubscriptionService";
import { getServices, Service } from "../../../../services/serviceService";
import { PaginationMeta } from "../../../../types/api";

const PER_PAGE = 15;

interface PlanFormValues {
  name: string;
  description: string;
  price_display: string;
  commission_basis_display: string;
  included_service_ids: number[];
  monthly_visit_limit: string;
  is_active: boolean;
}

const emptyValues = (): PlanFormValues => ({
  name: "",
  description: "",
  price_display: "",
  commission_basis_display: "",
  included_service_ids: [],
  monthly_visit_limit: "",
  is_active: true,
});

const ClientSubscriptionPlansList = () => {
  const { t, i18n } = useTranslation();
  const [plans, setPlans] = useState<ClientSubscriptionPlan[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState<Service[]>([]);
  const [canvasOpen, setCanvasOpen] = useState(false);
  const [editing, setEditing] = useState<ClientSubscriptionPlan | null>(null);
  const [metrics, setMetrics] = useState<ClubMetrics | null>(null);

  document.title = `${t("studio.clientSubscriptionPlans.pageTitle")} | Adatis`;

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getClientSubscriptionPlans({
        page,
        per_page: PER_PAGE,
      });
      setPlans(response.data ?? []);
      setMeta(response.meta ?? null);
    } catch (error: any) {
      toast.error(error?.data || t("studio.clientSubscriptionPlans.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [page, t]);

  const loadMetrics = useCallback(async () => {
    try {
      const response = await getClientSubscriptionMetrics();
      setMetrics(response.data ?? null);
    } catch {
      setMetrics(null);
    }
  }, []);

  useEffect(() => {
    void loadPlans();
  }, [loadPlans]);

  useEffect(() => {
    void loadMetrics();
  }, [loadMetrics]);

  useEffect(() => {
    void (async () => {
      try {
        const response = await getServices({ per_page: 100 });
        setServices(
          (response.data ?? []).filter((service) => service.is_active !== false)
        );
      } catch {
        setServices([]);
      }
    })();
  }, []);

  const validation = useFormik<PlanFormValues>({
    enableReinitialize: true,
    initialValues: editing
      ? {
          name: editing.name,
          description: editing.description ?? "",
          price_display: formatCentsToMoneyInput(editing.price),
          commission_basis_display:
            editing.commission_basis_cents != null
              ? formatCentsToMoneyInput(editing.commission_basis_cents)
              : "",
          included_service_ids: editing.benefits?.included_service_ids ?? [],
          monthly_visit_limit:
            editing.benefits?.monthly_visit_limit != null
              ? String(editing.benefits.monthly_visit_limit)
              : "",
          is_active: editing.is_active,
        }
      : emptyValues(),
    validationSchema: Yup.object({
      name: Yup.string().required(t("studio.clientSubscriptionPlans.validation.name")),
      price_display: Yup.string().required(
        t("studio.clientSubscriptionPlans.validation.price")
      ),
      included_service_ids: Yup.array()
        .of(Yup.number())
        .min(1, t("studio.clientSubscriptionPlans.validation.services")),
    }),
    onSubmit: async (values) => {
      const price = parseMoneyInputToCents(values.price_display);
      if (price == null) {
        toast.error(t("studio.clientSubscriptionPlans.validation.price"));
        return;
      }

      const commission =
        values.commission_basis_display.trim() === ""
          ? null
          : parseMoneyInputToCents(values.commission_basis_display);

      const payload = {
        name: values.name.trim(),
        description: values.description.trim() || null,
        price,
        billing_interval: "monthly",
        benefits: {
          included_service_ids: values.included_service_ids,
          monthly_visit_limit:
            values.monthly_visit_limit.trim() === ""
              ? null
              : Number(values.monthly_visit_limit),
        },
        commission_basis_cents: commission,
        is_active: values.is_active,
      };

      setSaving(true);
      try {
        if (editing) {
          await updateClientSubscriptionPlan(editing.id, payload);
          toast.success(t("studio.clientSubscriptionPlans.toast.updated"));
        } else {
          await createClientSubscriptionPlan(payload);
          toast.success(t("studio.clientSubscriptionPlans.toast.created"));
        }
        setCanvasOpen(false);
        setEditing(null);
        await loadPlans();
        await loadMetrics();
      } catch (error: any) {
        toast.error(
          error?.data?.error?.message ||
            error?.data ||
            t("studio.clientSubscriptionPlans.toast.saveError")
        );
      } finally {
        setSaving(false);
      }
    },
  });

  const serviceNameById = useMemo(() => {
    const map = new Map<number, string>();
    services.forEach((service) => map.set(service.id, service.name));
    return map;
  }, [services]);

  const formatMoney = useCallback(
    (cents: number) => formatMoneyFromCents(cents, i18n.language),
    [i18n.language]
  );

  const openCreate = () => {
    setEditing(null);
    validation.resetForm({ values: emptyValues() });
    setCanvasOpen(true);
  };

  const openEdit = (plan: ClientSubscriptionPlan) => {
    setEditing(plan);
    setCanvasOpen(true);
  };

  const handleDeactivate = async (plan: ClientSubscriptionPlan) => {
    try {
      await deleteClientSubscriptionPlan(plan.id);
      toast.success(t("studio.clientSubscriptionPlans.toast.deactivated"));
      await loadPlans();
    } catch (error: any) {
      toast.error(
        error?.data?.error?.message ||
          error?.data ||
          t("studio.clientSubscriptionPlans.toast.deactivateError")
      );
    }
  };

  const toggleService = (serviceId: number) => {
    const current = validation.values.included_service_ids;
    const next = current.includes(serviceId)
      ? current.filter((id) => id !== serviceId)
      : [...current, serviceId];
    validation.setFieldValue("included_service_ids", next);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("studio.clientSubscriptionPlans.pageTitle")}
          pageTitle={t("menu.catalog.root")}
        />
        {metrics ? (
          <Row className="mb-3">
            <Col md={3} sm={6} className="mb-3 mb-md-0">
              <Card className="mb-0 h-100">
                <CardBody>
                  <p className="text-muted text-uppercase fw-semibold fs-12 mb-1">
                    {t("studio.clientSubscriptionPlans.metrics.mrr")}
                  </p>
                  <h4 className="mb-1">
                    {formatMoney(metrics.mrr_cents)}
                  </h4>
                  <p className="text-muted small mb-0">
                    {t("studio.clientSubscriptionPlans.metrics.mrrHint", {
                      active: formatMoney(metrics.mrr_active_cents),
                      pastDue: formatMoney(metrics.mrr_past_due_cents),
                    })}
                  </p>
                </CardBody>
              </Card>
            </Col>
            <Col md={3} sm={6} className="mb-3 mb-md-0">
              <Card className="mb-0 h-100">
                <CardBody>
                  <p className="text-muted text-uppercase fw-semibold fs-12 mb-1">
                    {t("studio.clientSubscriptionPlans.metrics.active")}
                  </p>
                  <h4 className="mb-1">{metrics.active_count}</h4>
                  <p className="text-muted small mb-0">
                    {t("studio.clientSubscriptionPlans.metrics.pastDue", {
                      count: metrics.past_due_count,
                    })}
                  </p>
                </CardBody>
              </Card>
            </Col>
            <Col md={3} sm={6} className="mb-3 mb-md-0">
              <Card className="mb-0 h-100">
                <CardBody>
                  <p className="text-muted text-uppercase fw-semibold fs-12 mb-1">
                    {t("studio.clientSubscriptionPlans.metrics.churn")}
                  </p>
                  <h4 className="mb-1">{metrics.churn_this_month_count}</h4>
                  <p className="text-muted small mb-0">
                    {t("studio.clientSubscriptionPlans.metrics.churnHint")}
                  </p>
                </CardBody>
              </Card>
            </Col>
            <Col md={3} sm={6}>
              <Card className="mb-0 h-100">
                <CardBody>
                  <p className="text-muted text-uppercase fw-semibold fs-12 mb-1">
                    {t("studio.clientSubscriptionPlans.metrics.collected")}
                  </p>
                  <h4 className="mb-1">
                    {formatMoney(metrics.revenue_collected_this_month_cents)}
                  </h4>
                  <p className="text-muted small mb-0">
                    {t("studio.clientSubscriptionPlans.metrics.collectedHint")}
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        ) : null}
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h5 className="mb-1">
                      {t("studio.clientSubscriptionPlans.heading")}
                    </h5>
                    <p className="text-muted mb-0">
                      {t("studio.clientSubscriptionPlans.subtitle")}
                    </p>
                  </div>
                  <Button color="primary" onClick={openCreate}>
                    {t("studio.clientSubscriptionPlans.actions.create")}
                  </Button>
                </div>

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : plans.length === 0 ? (
                  <div className="text-center py-5">
                    <p className="text-muted mb-2">
                      {t("studio.clientSubscriptionPlans.empty")}
                    </p>
                    <p className="text-muted small mb-3">
                      {t("studio.clientSubscriptionPlans.emptyHint")}
                    </p>
                    <Button color="primary" onClick={openCreate}>
                      {t("studio.clientSubscriptionPlans.actions.create")}
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table className="align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("studio.clientSubscriptionPlans.table.name")}</th>
                            <th>{t("studio.clientSubscriptionPlans.table.price")}</th>
                            <th>{t("studio.clientSubscriptionPlans.table.services")}</th>
                            <th>{t("studio.clientSubscriptionPlans.table.status")}</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {plans.map((plan) => (
                            <tr key={plan.id}>
                              <td>
                                <strong>{plan.name}</strong>
                                {plan.description ? (
                                  <div className="text-muted small">
                                    {plan.description}
                                  </div>
                                ) : null}
                              </td>
                              <td>{formatMoney(plan.price)}</td>
                              <td>
                                {(plan.benefits?.included_service_ids ?? [])
                                  .map(
                                    (id) =>
                                      serviceNameById.get(id) ?? `#${id}`
                                  )
                                  .join(", ") || "—"}
                              </td>
                              <td>
                                <Badge
                                  color={plan.is_active ? "success" : "secondary"}
                                  className="text-uppercase"
                                >
                                  {plan.is_active
                                    ? t("studio.clientSubscriptionPlans.status.active")
                                    : t(
                                        "studio.clientSubscriptionPlans.status.inactive"
                                      )}
                                </Badge>
                              </td>
                              <td className="text-end">
                                <Button
                                  color="soft-primary"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => openEdit(plan)}
                                >
                                  {t("studio.clientSubscriptionPlans.actions.edit")}
                                </Button>
                                {plan.is_active ? (
                                  <Button
                                    color="soft-danger"
                                    size="sm"
                                    onClick={() => void handleDeactivate(plan)}
                                  >
                                    {t(
                                      "studio.clientSubscriptionPlans.actions.deactivate"
                                    )}
                                  </Button>
                                ) : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    {meta && meta.last_page > 1 ? (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <Button
                          color="light"
                          size="sm"
                          disabled={page <= 1}
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                          {t("studio.clientSubscriptionPlans.pagination.previous")}
                        </Button>
                        <span className="text-muted small">
                          {page} / {meta.last_page}
                        </span>
                        <Button
                          color="light"
                          size="sm"
                          disabled={page >= meta.last_page}
                          onClick={() => setPage((p) => p + 1)}
                        >
                          {t("studio.clientSubscriptionPlans.pagination.next")}
                        </Button>
                      </div>
                    ) : null}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Offcanvas
        direction="end"
        isOpen={canvasOpen}
        toggle={() => setCanvasOpen(false)}
        style={{ width: 420 }}
      >
        <OffcanvasHeader toggle={() => setCanvasOpen(false)}>
          {editing
            ? t("studio.clientSubscriptionPlans.form.editTitle")
            : t("studio.clientSubscriptionPlans.form.createTitle")}
        </OffcanvasHeader>
        <OffcanvasBody>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              void validation.handleSubmit();
            }}
          >
            <div className="mb-3">
              <Label>{t("studio.clientSubscriptionPlans.form.name")}</Label>
              <Input
                name="name"
                value={validation.values.name}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  !!(validation.touched.name && validation.errors.name)
                }
              />
              <FormFeedback>{validation.errors.name}</FormFeedback>
            </div>
            <div className="mb-3">
              <Label>
                {t("studio.clientSubscriptionPlans.form.description")}
              </Label>
              <Input
                name="description"
                value={validation.values.description}
                onChange={validation.handleChange}
              />
            </div>
            <div className="mb-3">
              <Label>{t("studio.clientSubscriptionPlans.form.price")}</Label>
              <MoneyInput
                locale={i18n.language}
                value={validation.values.price_display}
                onChange={(value) =>
                  validation.setFieldValue("price_display", value)
                }
                onBlur={() => validation.setFieldTouched("price_display", true)}
                invalid={
                  !!(
                    validation.touched.price_display &&
                    validation.errors.price_display
                  )
                }
              />
              <FormFeedback className="d-block">
                {validation.errors.price_display}
              </FormFeedback>
            </div>
            <div className="mb-3">
              <Label>
                {t("studio.clientSubscriptionPlans.form.commissionBasis")}
              </Label>
              <MoneyInput
                locale={i18n.language}
                value={validation.values.commission_basis_display}
                onChange={(value) =>
                  validation.setFieldValue("commission_basis_display", value)
                }
              />
              <div className="form-text">
                {t("studio.clientSubscriptionPlans.form.commissionBasisHint")}
              </div>
            </div>
            <div className="mb-3">
              <Label>
                {t("studio.clientSubscriptionPlans.form.visitLimit")}
              </Label>
              <Input
                type="number"
                min={1}
                name="monthly_visit_limit"
                value={validation.values.monthly_visit_limit}
                onChange={validation.handleChange}
                placeholder={t(
                  "studio.clientSubscriptionPlans.form.visitLimitPlaceholder"
                )}
              />
            </div>
            <div className="mb-3">
              <Label>
                {t("studio.clientSubscriptionPlans.form.services")}
              </Label>
              {services.length === 0 ? (
                <p className="text-muted small mb-0">
                  {t("studio.clientSubscriptionPlans.form.noServices")}
                </p>
              ) : (
                <div className="border rounded p-2" style={{ maxHeight: 220, overflowY: "auto" }}>
                  {services.map((service) => (
                    <div className="form-check" key={service.id}>
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id={`club-svc-${service.id}`}
                        checked={validation.values.included_service_ids.includes(
                          service.id
                        )}
                        onChange={() => toggleService(service.id)}
                      />
                      <Label
                        className="form-check-label"
                        htmlFor={`club-svc-${service.id}`}
                      >
                        {service.name}
                      </Label>
                    </div>
                  ))}
                </div>
              )}
              {validation.touched.included_service_ids &&
              validation.errors.included_service_ids ? (
                <div className="invalid-feedback d-block">
                  {String(validation.errors.included_service_ids)}
                </div>
              ) : null}
            </div>
            <div className="form-check form-switch mb-4">
              <Input
                type="checkbox"
                className="form-check-input"
                id="club-plan-active"
                checked={validation.values.is_active}
                onChange={(e) =>
                  validation.setFieldValue("is_active", e.target.checked)
                }
              />
              <Label className="form-check-label" htmlFor="club-plan-active">
                {t("studio.clientSubscriptionPlans.form.active")}
              </Label>
            </div>
            <div className="d-flex gap-2">
              <Button color="primary" type="submit" disabled={saving}>
                {saving ? (
                  <Spinner size="sm" />
                ) : (
                  t("studio.clientSubscriptionPlans.actions.save")
                )}
              </Button>
              <Button
                color="light"
                type="button"
                onClick={() => setCanvasOpen(false)}
              >
                {t("studio.clientSubscriptionPlans.actions.cancel")}
              </Button>
            </div>
          </Form>
        </OffcanvasBody>
      </Offcanvas>
      <ToastContainer />
    </div>
  );
};

export default ClientSubscriptionPlansList;
